'use client'

import { localeFlags, localeNames, type Locale } from '@/i18n/config'
import { usePathname, useRouter } from '@/i18n/routing'
import { useLocale } from 'next-intl'
import { useParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

export function LanguageToggle({ openUpwards = false }: { openUpwards?: boolean }) {
  const locale = useLocale() as Locale
  const pathname = usePathname()
  // Auf dynamischen Routen liefert usePathname() das Template (z.B. /projekte/[slug]);
  // router.push braucht dann die konkreten Params, sonst wirft next-intl zur Laufzeit
  const params = useParams()
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLanguageChange = (newLocale: Locale) => {
    // @ts-expect-error -- pathname und params stammen aus derselben aktuellen Route
    // und passen daher immer zusammen; die statische Typprüfung kann das nicht wissen.
    router.push({ pathname, params }, { locale: newLocale })
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-KMRSx24-GreyLight hover:text-accent transition-colors"
        aria-label="Change language"
      >
        <span>{localeFlags[locale]}</span>
        <span className="hidden sm:inline">{localeNames[locale]}</span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute right-0 w-40 bg-KMRS-Darkmode border border-accent rounded-lg shadow-lg z-50 ${openUpwards ? 'bottom-full mb-2' : 'mt-2'}`}>
          {Object.entries(localeNames).map(([key, name]) => {
            const localeKey = key as Locale
            return (
              <button
                key={key}
                onClick={() => handleLanguageChange(localeKey)}
                className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-accent/10 transition-colors ${
                  locale === key ? 'text-accent' : 'text-KMRSx24-GreyLight'
                } ${key === 'de' ? 'rounded-t-lg' : 'rounded-b-lg'}`}
              >
                <span>{localeFlags[localeKey]}</span>
                <span>{name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
